import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import EventDetail from "./components/EventDetail";
import BusinessNetworkEventDetail from "./BusinessNetworkEventDetail";
import HousingDevelopmentEventDetail from "./housingDevelopment/HousingDevelopmentEventDetail";
import { getApprovedEventById } from "./eventService";
import { eventDetailPath, eventIdFromDetailParam } from "./eventRoutes";
import { isBusinessNetworkEvent } from "./businessNetworkEvents";
import { isHousingDevelopmentEvent } from "./housingDevelopment/housingDevelopmentUtils";
import { getLocalHousingDevelopmentEventFallback } from "./housingDevelopment/housingDevelopmentService";

export function eventDetailBackLink(event, eventId = "") {
  const isHousingDevelopment = isHousingDevelopmentEvent(event, eventId);
  const isBusinessNetwork = event
    ? isBusinessNetworkEvent(event)
    : String(eventId).startsWith("business-network-");

  return isHousingDevelopment
    ? { to: "/housing-development", label: "← Back to Housing & Development" }
    : isBusinessNetwork
    ? { to: "/business-network", label: "← Back to Business Network" }
    : { to: "/events", label: "← Back to events" };
}

export function canonicalEventDestination(canonicalPath, location) {
  return {
    pathname: canonicalPath,
    search: location.search || "",
    hash: location.hash || "",
  };
}

export default function EventDetailPage() {
  const { eventReference = "" } = useParams();
  const eventId = eventIdFromDetailParam(eventReference);
  const navigate = useNavigate();
  const location = useLocation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setEvent(null);

    getApprovedEventById(eventId)
      .then((approvedEvent) => {
        if (!active) return;
        setEvent(
          approvedEvent || getLocalHousingDevelopmentEventFallback(eventId)
        );
        setLoading(false);
      })
      .catch((loadError) => {
        console.error("Unable to load approved event", loadError);
        if (!active) return;
        const localFallback = getLocalHousingDevelopmentEventFallback(eventId);
        if (localFallback) {
          setEvent(localFallback);
          setLoading(false);
          return;
        }
        setError("This event is temporarily unavailable. Please try again shortly.");
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [eventId, attempt]);

  useEffect(() => {
    if (!event) return;
    const canonicalPath = eventDetailPath(event);
    if (canonicalPath && location.pathname !== canonicalPath) {
      navigate(canonicalEventDestination(canonicalPath, location), { replace: true });
    }
  }, [event, location, navigate]);

  const backLink = eventDetailBackLink(event, eventId);

  return (
    <section aria-label="Event details">
      <Link to={backLink.to} className="mb-6 inline-flex min-h-11 items-center rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10">
        {backLink.label}
      </Link>

      {loading ? <p role="status" className="text-white/70">Loading event…</p> : null}

      {!loading && error ? (
        <div role="alert" className="rounded-xl border border-amber-300/30 bg-amber-300/10 p-4 text-amber-100">
          <p>{error}</p>
          <button type="button" onClick={() => setAttempt((value) => value + 1)} className="mt-3 rounded-lg border border-amber-100/30 bg-black/20 px-3 py-2 text-sm font-semibold">
            Retry
          </button>
        </div>
      ) : null}

      {!loading && !error && !event ? (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center">
          <h1 className="text-3xl font-black">Event not available</h1>
          <p className="mt-3 text-white/70">This event may have ended, been removed, or the link may be incorrect.</p>
          <Link to={backLink.to} className="mt-5 inline-flex min-h-12 items-center rounded-xl bg-[#E63946] px-5 py-3 font-bold text-white">
            {backLink.to === "/business-network"
              ? "View the Business Network"
              : backLink.to === "/housing-development"
              ? "View Housing & Development"
              : "Browse current events"}
          </Link>
        </div>
      ) : null}

      {!loading && !error && event ? (
        isHousingDevelopmentEvent(event, eventId) ? (
          <HousingDevelopmentEventDetail event={event} />
        ) : isBusinessNetworkEvent(event) ? (
          <BusinessNetworkEventDetail event={event} />
        ) : (
          <EventDetail event={event} />
        )
      ) : null}
    </section>
  );
}
